import { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslations } from 'next-intl';
import ProductImage from '../products/ProductImage';
import wooCommerceStore from '@/stores/woocommerceStore';
import { formatPrice } from '@/utils/misc';

const CartNotification = observer(() => {
  const t = useTranslations('Cart');
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleCartChange = () => {
      const item = wooCommerceStore.lastModifiedItem;
      const type = wooCommerceStore.lastModificationType;
      const cartOpen = wooCommerceStore.isCartOpen;
      if (item && (type === 'add' || type === 'update') && type !== 'clear' && !cartOpen) {
        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        setIsVisible(true);

        // Set new timeout
        timeoutRef.current = setTimeout(() => {
          setIsVisible(false);
        }, 3000);
      }
    };

    // Listen for cart changes
    const unsubscribe = wooCommerceStore.subscribeToCart(handleCartChange);

    return () => {
      // Clean up timeout and subscription on unmount
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const item = wooCommerceStore.lastModifiedItem;
  const type = wooCommerceStore.lastModificationType;
  const cartOpen = wooCommerceStore.isCartOpen;

  const shouldShow = isVisible && item && type !== 'remove' && type !== 'clear' && !cartOpen;

  const productType = item?.type ?? "";
  let img = (item?.images?.[0]?.src) ? item.images[0] : {};
  let name = item?.name ?? '';

  if (productType === 'variable' || productType === 'variation') {
    img = (item?.image?.src) ? item.image : (item?.parent_images?.[0] ?? {});
    name = item?.parent_name ?? item.name;
  }

  const getMessage = () => {
    if (type === 'add') return t('added_to_cart');
    if (type === 'update') return t('updated_in_cart');
    return '';
  };

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          key="cart-notification"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.2 }}
          className="absolute -bottom-20 right-0 rtl:left-0 rtl:right-auto bg-white rounded-lg shadow-lg p-2 max-w-sm min-w-[240px] z-50 border border-gray-200 cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => wooCommerceStore.toggleCart()}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              wooCommerceStore.toggleCart();
            }
          }}
        >
          <div className="flex items-center space-x-4">
            <div className="relative w-16 h-16 me-3">
              <ProductImage
                img={img}
                productName={item?.name}
                width={64}
                height={64}
                className="h-16 w-16 object-contain rounded-lg mr-2 rtl:mr-0 rtl:ml-2"
              />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900">{name}</h4>
              <p className="text-sm text-gray-500">{getMessage()}</p>
              <div className="flex items-center mt-1">
                <span className="text-sm font-medium text-gray-900">
                  {formatPrice(item.price * item.quantity)}
                </span>
                {item.quantity > 1 && (
                  <span className="text-sm text-gray-500 ml-2">
                    × {item.quantity}
                  </span>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default CartNotification;